import Vue from 'vue';
import moment from 'moment';
import Vuex from 'vuex';
import { removeItemFromArrayByValue } from '@/components/helpers';
import { cloneDeep } from 'lodash';

import { PLACEHOLDER_EMPTY_WORKFLOW_ID, DEFAULT_NODETYPE_VERSION } from '@/constants';

import {
	IConnection,
	IConnections,
	IDataObject,
	INodeConnections,
	INodeIssueData,
	INodeTypeDescription,
	IRunData,
	ITelemetrySettings,
	ITaskData,
	IWorkflowSettings,
} from 'n8n-workflow';

import {
	IExecutionResponse,
	IExecutionsCurrentSummaryExtended,
	IRootState,
	IMenuItem,
	INodeUi,
	INodeUpdatePropertiesInformation,
	IPushDataExecutionFinished,
	IPushDataNodeExecuteAfter,
	IUpdateInformation,
	IWorkflowDb,
	XYPosition,
	IRestApiContext,
} from './Interface';

import credentials from './modules/credentials';
import tags from './modules/tags';
import settings from './modules/settings';
import ui from './modules/ui';
import workflows from './modules/workflows';
import aliareAccount from './modules/aliareAccount';
import WebStorage from "@/common/WebStorage";
import TokenService from '@/services/token.service';

import variable from './modules/variable';
import execution from './modules/executions';
import authModule from './modules/auth';
import coreModule from './modules/core';
import alert from './modules/alert';
import project from './modules/project';
import settingsaliareModule from './modules/settingsaliare';
import { appConfig } from '@/config/appConfig';

Vue.use(Vuex);

const state: IRootState = {
	selectedFunctionalities: [],
	activeExecutions: [],
	activeWorkflows: [],
	activeActions: [],
	activeNode: null,
	activeCredentialType: null,
	// @ts-ignore
	baseUrl: appConfig('backendURL'),
	defaultLocale: 'en',
	endpointWebhook: 'webhook',
	endpointWebhookTest: 'webhook-test',
	executionId: null,
	executingNode: '',
	executionWaitingForWebhook: false,
	pushConnectionActive: true,
	saveDataErrorExecution: 'all',
	saveDataSuccessExecution: 'all',
	saveManualExecutions: false,
	timezone: 'America/Fortaleza',
	errorWorkflow: '',
	stateIsDirty: false,
	hasUnpublishedChanges: false,
	executionTimeout: -1,
	maxExecutionTimeout: Number.MAX_SAFE_INTEGER,
	oauthCallbackUrls: {},
	n8nMetadata: {},
	workflowExecutionData: null,
	workflowVersionData: null,
	versionInfo: {
		nomePublicacao: null,
		descricaoPublicacao: null,
	},
	versionToPublish: null as string | null,
	workflowIdToPublish: null as string | null,
	versionWorkflowDataToEdit: null as { nodes: any[], connections: any } | null,
	workflowAtivo: false,
	originalDataWorkflow: {},
	workflowSaveAutomatic: false,
	lastSelectedNode: null,
	lastSelectedNodeOutputIndex: null,
	workflowCanUndo: false,
	workflowCanRedo: false,
	credentialOptions: [],
	nodesIntegration: [],
	nodeIndex: [],
	nodeTypes: [],
	nodeViewOffsetPosition: [0, 0],
	nodeViewMoveInProgress: false,
	selectedNodes: [],
	sessionId: Math.random().toString(36).substring(2, 15),
	tenantId:  WebStorage.getItem("tenantId"),
	urlBaseWebhook: 'http://localhost:5678/',
	workflow: {
		id: PLACEHOLDER_EMPTY_WORKFLOW_ID,
		name: '',
		active: false,
		createdAt: -1,
		updatedAt: -1,
		connections: {},
		nodes: [],
		settings: {},
		tags: [],
	},
	projectData: {},
	workflowData: {
		projetoId: '',
		nome: '',
		id: '',
		permission: '',
		workflowData: {
			id: '',
			active: false,
			name: '',
			nodes: [],
			connections: {},
			settings: {},
			tags: [],
		},
	},
	WorkflowModifiedDate: '',
	sidebarMenuItems: [],
	instanceId: '',
	telemetry: null,
	accountName: WebStorage.getItem("accountName"),
	data403: {
		show: false,
	},
	alertData: {
		message: '',
		status: '',
		link: {},
	},
	history: {
		undo: [],
		current: [],
		redo: [],
	},
	showLoadingCredentials: false,
	multiAccount: false,
	loadingData: {},
	routeName: '',
	projectName: '',
	modalData: {},
	userData: {},
	userModalName: '',
	sendUserData: {},
	dataFilterPanel: {
		date: {
			dateString: 'today',
			dateStart: moment().startOf('day').format('YYYY-MM-DD HH:mm'),
			dateEnd: moment().endOf('day').format('YYYY-MM-DD HH:mm'),
			modeDate: 'dateOption',
		},
		selectedOrganizationIds: ['null'],
		selectedWorkspaceIds: ['null'],
		selectedProjectIds: ['null'],
		selectedFlowIds: ['null'],
		resultExecutionFilter: ['null'],
		executionModeFilter: ['null'],
		translateResultExecutionFilter: ['null'],
		translateModeExecutionFilter: ['null'],
	},
	workspace: JSON.parse(WebStorage.getItem('workspace')) ?? {},
	filters: {},
};

const modules = {
	credentials,
	tags,
	settings,
	workflows,
	aliareAccount,
	ui,
	alert,
	variable,
	execution,
	project,
	auth: { ...authModule, namespaced: true },
	core: { ...coreModule, namespaced: true },
	settingsaliare: { ...settingsaliareModule, namespaced: true },
};

export const store = new Vuex.Store({
	strict: process.env.NODE_ENV !== 'production',
	modules,
	state,
	mutations: {
		// Active Actions
		addActiveAction (state, action: string) {
			if (!state.activeActions.includes(action)) {
				state.activeActions.push(action);
			}
		},

		activeAlert (state, payload) {
			state.alertData.message = payload?.message;
			state.alertData.status = payload?.status || 'success';
			state.alertData.link = payload?.link;
		},

		addToHistory(state, data) {
			const undoItem = cloneDeep(state.history.current[0]);

			const MAX_HISTORY_SIZE = 50;

			if (state.history.undo.length >= MAX_HISTORY_SIZE) {
				state.history.undo.shift();
			}

			state.history.undo.push(undoItem);
			state.history.current = [data];
			state.history.redo = [];
		},

		addCurrentHistory(state, data) {
			state.history.current = data
		},

		redoAction(state) {
			if (state.history.redo.length > 0) {
				const redoItem = state.history.redo.pop();
				state.history.undo.push(cloneDeep(state.history.current[0]));
				state.history.current = [redoItem];
			}
		},

		undoAction(state) {
			if (state.history.undo.length > 0) {
				const undoItem = state.history.undo.pop();
				state.history.redo.push(cloneDeep(state.history.current[0]));
				state.history.current = [undoItem];
			}
		},

		activeLoadingCredentials (state, payload) {
			state.showLoadingCredentials = payload?.showLoading;
		},

		removeActiveAction (state, action: string) {
			const actionIndex = state.activeActions.indexOf(action);
			if (actionIndex !== -1) {
				state.activeActions.splice(actionIndex, 1);
			}
		},

		// Active Executions
		addActiveExecution (state, newActiveExecution: IExecutionsCurrentSummaryExtended) {
			// Check if the execution exists already
			const activeExecution = state.activeExecutions.find(execution => {
				return execution.id === newActiveExecution.id;
			});

			if (activeExecution !== undefined) {
				// Exists already so no need to add it again
				if (activeExecution.workflowName === undefined) {
					activeExecution.workflowName = newActiveExecution.workflowName;
				}
				return;
			}

			state.activeExecutions.unshift(newActiveExecution);
		},
		finishActiveExecution (state, finishedActiveExecution: IPushDataExecutionFinished) {
			// Find the execution to set to finished
			const activeExecution = state.activeExecutions.find(execution => {
				return execution.id === finishedActiveExecution.executionId;
			});



			if (activeExecution === undefined) {
				// The execution could not be found
				return;
			}

			if (finishedActiveExecution.executionId !== undefined) {
				Vue.set(activeExecution, 'id', finishedActiveExecution.executionId);
			}

			Vue.set(activeExecution, 'finished', finishedActiveExecution.data.finished || true);
			Vue.set(activeExecution, 'stoppedAt', finishedActiveExecution.data.stoppedAt);
		},
		setActiveExecutions (state, newActiveExecutions: IExecutionsCurrentSummaryExtended[]) {
			Vue.set(state, 'activeExecutions', newActiveExecutions);
		},

		updateSelectedFunctionality (state, selectedFunctionality: string) {
			const functionality = state.selectedFunctionalities.find(functionalityId => {
				return functionalityId === selectedFunctionality;
			});

			if (functionality) {
				removeItemFromArrayByValue(state.selectedFunctionalities, selectedFunctionality);
			} else {
				state.selectedFunctionalities.push(selectedFunctionality);
			}
		},

		// Active Workflows
		setActiveWorkflows (state, newActiveWorkflows: string[]) {
			state.activeWorkflows = newActiveWorkflows;
		},
		setWorkflowActive (state, workflowId: string) {
			state.stateIsDirty = false;
			const index = state.activeWorkflows.indexOf(workflowId);
			if (index === -1) {
				state.activeWorkflows.push(workflowId);
			}
		},
		setWorkflowInactive (state, workflowId: string) {
			const index = state.activeWorkflows.indexOf(workflowId);
			if (index !== -1) {
				state.selectedNodes.splice(index, 1);
			}
		},

		// Set state condition dirty or not
		// ** Dirty: if current workflow state has been synchronized with database AKA has it been saved
		setStateDirty (state, dirty : boolean) {
			state.stateIsDirty = dirty;
		},

		setHasUnpublishedChanges(state, hasChanges: boolean) {
			state.hasUnpublishedChanges = hasChanges;
		},

		addNodeIntegration(state, node: INodeUi) {
			state.nodesIntegration.push(node);
		},

		clearNodeIntegration(state) {
			state.nodesIntegration = [];
		},

		setData403(state, data: object) {
			state.data403 = data;
		},

		// Selected Nodes
		addSelectedNode (state, node: INodeUi) {
			state.selectedNodes.push(node);
		},
		removeNodeFromIntegration(state, node: INodeUi) {
			let index;
			for (index in state.nodesIntegration) {
				if (state.nodesIntegration[index].name === node.name) {
					state.nodesIntegration.splice(parseInt(index, 10), 1);
					break;
				}
			}
		},
		removeNodeFromSelection (state, node: INodeUi) {
			let index;
			for (index in state.selectedNodes) {
				if (state.selectedNodes[index].name === node.name) {
					state.selectedNodes.splice(parseInt(index, 10), 1);
					break;
				}
			}
		},
		resetSelectedNodes (state) {
			Vue.set(state, 'selectedNodes', []);
		},

		resetSelectedFunctionalities (state) {
			Vue.set(state, 'selectedFunctionalities', []);
		},

		// Active
		setActive (state, newActive: boolean) {
			state.workflow.active = newActive;
		},

		// Connections
		addConnection (state, data) {
			if (data.connection.length !== 2) {
				// All connections need two entries
				// TODO: Check if there is an error or whatever that is supposed to be returned
				return;
			}

			if (data.setStateDirty === true) {
				state.stateIsDirty = true;
			}

			const sourceData: IConnection = data.connection[0];
			const destinationData: IConnection = data.connection[1];

			// Check if source node and type exist already and if not add them
			if (!state.workflow.connections.hasOwnProperty(sourceData.node)) {
				Vue.set(state.workflow.connections, sourceData.node, {});
			}
			if (!state.workflow.connections[sourceData.node].hasOwnProperty(sourceData.type)) {
				Vue.set(state.workflow.connections[sourceData.node], sourceData.type, []);
			}
			if (state.workflow.connections[sourceData.node][sourceData.type].length < (sourceData.index + 1)) {
				for (let i = state.workflow.connections[sourceData.node][sourceData.type].length; i <= sourceData.index; i++) {
					state.workflow.connections[sourceData.node][sourceData.type].push([]);
				}
			}

			// Check if the same connection exists already
			const checkProperties = ['index', 'node', 'type'];
			let propertyName: string;
			let connectionExists = false;
			connectionLoop:
			for (const existingConnection of state.workflow.connections[sourceData.node][sourceData.type][sourceData.index]) {
				for (propertyName of checkProperties) {
					if ((existingConnection as any)[propertyName] !== (destinationData as any)[propertyName]) { // tslint:disable-line:no-any
						continue connectionLoop;
					}
				}
				connectionExists = true;
				break;
			}

			// Add the new connection if it does not exist already
			if (connectionExists === false) {
				state.workflow.connections[sourceData.node][sourceData.type][sourceData.index].push(destinationData);
			}

		},
		removeConnection (state, data) {
			const sourceData = data.connection[0];
			const destinationData = data.connection[1];

			if (!state.workflow.connections.hasOwnProperty(sourceData.node)) {
				return;
			}
			if (!state.workflow.connections[sourceData.node].hasOwnProperty(sourceData.type)) {
				return;
			}
			if (state.workflow.connections[sourceData.node][sourceData.type].length < (sourceData.index + 1)) {
				return;
			}

			state.stateIsDirty = true;

			const connections = state.workflow.connections[sourceData.node][sourceData.type][sourceData.index];
			for (const index in connections) {
				if (connections[index].node === destinationData.node && connections[index].type === destinationData.type && connections[index].index === destinationData.index) {
					// Found the connection to remove
					connections.splice(parseInt(index, 10), 1);
				}
			}

		},
		removeAllConnections (state, data) {
			if (data && data.setStateDirty === true) {
				state.stateIsDirty = true;
			}
			state.workflow.connections = {};
		},
		removeAllNodeConnection (state, node: INodeUi) {
			state.stateIsDirty = true;
			// Remove all source connections
			if (state.workflow.connections.hasOwnProperty(node.name)) {
				delete state.workflow.connections[node.name];
			}

			// Remove all destination connections
			const indexesToRemove = [];
			let sourceNode: string, type: string, sourceIndex: string, connectionIndex: string, connectionData: IConnection;
			for (sourceNode of Object.keys(state.workflow.connections)) {
				for (type of Object.keys(state.workflow.connections[sourceNode])) {
					for (sourceIndex of Object.keys(state.workflow.connections[sourceNode][type])) {
						indexesToRemove.length = 0;
						for (connectionIndex of Object.keys(state.workflow.connections[sourceNode][type][parseInt(sourceIndex, 10)])) {
							connectionData = state.workflow.connections[sourceNode][type][parseInt(sourceIndex, 10)][parseInt(connectionIndex, 10)];
							if (connectionData.node === node.name) {
								indexesToRemove.push(connectionIndex);
							}
						}

						indexesToRemove.forEach((index) => {
							state.workflow.connections[sourceNode][type][parseInt(sourceIndex, 10)].splice(parseInt(index, 10), 1);
						});
					}
				}
			}
		},

		renameNodeSelectedAndExecution (state, nameData) {
			state.stateIsDirty = true;
			// If node has any WorkflowResultData rename also that one that the data
			// does still get displayed also after node got renamed
			if (state.workflowExecutionData !== null && state.workflowExecutionData.data.resultData.runData.hasOwnProperty(nameData.old)) {
				state.workflowExecutionData.data.resultData.runData[nameData.new] = state.workflowExecutionData.data.resultData.runData[nameData.old];
				delete state.workflowExecutionData.data.resultData.runData[nameData.old];
			}

			// In case the renamed node was last selected set it also there with the new name
			if (state.lastSelectedNode === nameData.old) {
				state.lastSelectedNode = nameData.new;
			}
		},

		resetAllNodesIssues (state) {
			state.workflow.nodes.forEach((node) => {
				node.issues = undefined;
			});

			return true;
		},

		setNodeIssue (state, nodeIssueData: INodeIssueData) {

			const node = state.workflow.nodes.find(node => {
				return node.name === nodeIssueData.node;
			});
			if (!node) {
				return false;
			}

			if (nodeIssueData.value === null) {
				// Remove the value if one exists
				if (node.issues === undefined || node.issues[nodeIssueData.type] === undefined) {
					// No values for type exist so nothing has to get removed
					return true;
				}

				// @ts-ignore
				Vue.delete(node.issues, nodeIssueData.type);
			} else {
				if (node.issues === undefined) {
					Vue.set(node, 'issues', {});
				}

				// Set/Overwrite the value
				Vue.set(node.issues!, nodeIssueData.type, nodeIssueData.value);
			}

			return true;
		},

		setProjectName (state, name: string) {
			state.projectName = name;
		},

		setLoadingData (state, data: object) {
			state.loadingData = data;
		},

		setModalData (state, data: object) {
			state.modalData = data;
		},

		setUserData (state, data: object) {
			state.userData = data;
		},

		setSendUserData (state, data: object) {
			state.sendUserData = data;
		},

		setDataFilterPanel (state, data: object) {
			state.dataFilterPanel = data;
		},

		setUserModalName (state, name: string) {
			state.userModalName = name;
		},

		// Id
		setWorkflowId (state, id: string) {
			state.workflow.id = id;
		},

		setWorkspace(state, data: object) {
			state.workspace = data;
		},

		setFilters(state, filters: object) {
			state.filters = filters;
		},

		setOpenRouteName (state, name: string) {
			state.routeName = name;
		},

		setMultiAccount (state, multiAccount: boolean) {
			state.multiAccount = multiAccount;
		},

		// workflowData Id
		setWorkflowDataId (state, id: string) {
			state.workflowData.id = id;
		},

		setWorkflowDataPermission (state, permission: string) {
			state.workflowData.permission = permission;
		},

		setWorkflowData(state, data: object) {
			state.workflowData = data;
		},

		// Projeto Id
		setWorkflowProjectId (state, id: string) {
			state.workflowData.projetoId = id;
		},

		setWorkflowProjectData (state, data: object) {
			state.projectData = data;
		},

		// Name
		setWorkflowName (state, data) {
			if (data.setStateDirty === true) {
				state.stateIsDirty = true;
			}
			state.workflow.name = data.newName;
		},

		setWorkflowAtivo (state, data) {
			state.workflowAtivo = data;
		},

		setOriginalDataWorkflow(state, data) {
			state.originalDataWorkflow = data;
		},
		setWorkflowSaveAutomatic(state, data) {
			state.workflowSaveAutomatic = data;
		},

		// replace invalid credentials in workflow
		replaceInvalidWorkflowCredentials(state, {credentials, invalid, type }) {
			state.workflow.nodes.forEach((node) => {
				if (!node.credentials || !node.credentials[type]) {
					return;
				}
				const nodeCredentials = node.credentials[type];

				if (typeof nodeCredentials === 'string' && nodeCredentials === invalid.name) {
					node.credentials[type] = credentials;
					return;
				}

				if (nodeCredentials.id === null) {
					if (nodeCredentials.name === invalid.name){
						node.credentials[type] = credentials;
					}
					return;
				}

				if (nodeCredentials.id === invalid.id) {
					node.credentials[type] = credentials;
				}
			});
		},

		// Nodes
		addNode (state, nodeData: INodeUi) {
			if (!nodeData.hasOwnProperty('name')) {
				// All nodes have to have a name
				// TODO: Check if there is an error or whatever that is supposed to be returned
				return;
			}

			state.workflow.nodes.push(nodeData);
		},
		removeNode (state, node: INodeUi) {
			for (let i = 0; i < state.workflow.nodes.length; i++) {
				if (state.workflow.nodes[i].name === node.name) {
					state.workflow.nodes.splice(i, 1);
					state.stateIsDirty = true;
					return;
				}
			}
		},
		removeAllNodes (state, data) {
			if (data.setStateDirty === true) {
				state.stateIsDirty = true;
			}
			state.workflow.nodes.splice(0, state.workflow.nodes.length);
		},
		updateNodeProperties (state, updateInformation: INodeUpdatePropertiesInformation) {
			// Find the node that should be updated
			const node = state.workflow.nodes.find(node => {
				return node.name === updateInformation.name;
			});

			if (node) {
				for (const key of Object.keys(updateInformation.properties)) {
					state.stateIsDirty = true;
					Vue.set(node, key, updateInformation.properties[key]);
				}
			}
		},
		setNodeValue (state, updateInformation: IUpdateInformation) {
			// Find the node that should be updated
			const node = state.workflow.nodes.find(node => {
				return node.name === updateInformation.name;
			});

			if (node === undefined || node === null) {
				throw new Error(`Node with the name "${updateInformation.name}" could not be found to set parameter.`);
			}

			state.stateIsDirty = true;
			Vue.set(node, updateInformation.key, updateInformation.value);
		},
		setNodeParameters (state, updateInformation: IUpdateInformation) {
			// Find the node that should be updated
			const node = state.workflow.nodes.find(node => {
				return node.name === updateInformation.name;
			});

			if (node === undefined || node === null) {
				throw new Error(`Node with the name "${updateInformation.name}" could not be found to set parameter.`);
			}

			state.stateIsDirty = true;
			Vue.set(node, 'parameters', updateInformation.value);
		},

		// Node-Index
		addToNodeIndex (state, nodeName: string) {
			state.nodeIndex.push(nodeName);
		},
		setNodeIndex (state, newData: { index: number, name: string | null}) {
			state.nodeIndex[newData.index] = newData.name;
		},
		resetNodeIndex (state) {
			Vue.set(state, 'nodeIndex', []);
		},

		resetAlertData (state) {
			Vue.set(state, 'alertData', {
				message: '',
				status: '',
				link: {},
			});
		},

		// Node-View
		setNodeViewMoveInProgress (state, value: boolean) {
			state.nodeViewMoveInProgress = value;
		},

		setNodeViewOffsetPosition (state, data) {
			state.nodeViewOffsetPosition = data.newOffset;
		},

		// Node-Types
		setNodeTypes (state, nodeTypes: INodeTypeDescription[]) {
			Vue.set(state, 'nodeTypes', nodeTypes);
		},

		setCredentialOptions (state, credentials: string) {
			state.credentialOptions.push(credentials);
		},


		// Active Execution
		setExecutingNode (state, executingNode: string) {
			state.executingNode = executingNode;
		},
		setExecutionWaitingForWebhook (state, newWaiting: boolean) {
			state.executionWaitingForWebhook = newWaiting;
		},

		setWorkflowCanUndo (state, data: boolean) {
			state.workflowCanUndo = data;
		},

		setWorkflowCanRedo (state, data: boolean) {
			state.workflowCanRedo = data;
		},
		setActiveExecutionId (state, executionId: string | null) {
			state.executionId = executionId;
		},

		// Push Connection
		setPushConnectionActive (state, newActive: boolean) {
			state.pushConnectionActive = newActive;
		},

		// Webhooks
		setUrlBaseWebhook (state, urlBaseWebhook: string) {
			Vue.set(state, 'urlBaseWebhook', urlBaseWebhook);
		},
		setEndpointWebhook (state, endpointWebhook: string) {
			Vue.set(state, 'endpointWebhook', endpointWebhook);
		},
		setEndpointWebhookTest (state, endpointWebhookTest: string) {
			Vue.set(state, 'endpointWebhookTest', endpointWebhookTest);
		},

		setSaveDataErrorExecution (state, newValue: string) {
			Vue.set(state, 'saveDataErrorExecution', newValue);
		},
		setSaveDataSuccessExecution (state, newValue: string) {
			Vue.set(state, 'saveDataSuccessExecution', newValue);
		},
		setSaveManualExecutions (state, saveManualExecutions: boolean) {
			Vue.set(state, 'saveManualExecutions', saveManualExecutions);
		},
		setTimezone (state, timezone: string) {
			Vue.set(state, 'timezone', timezone);
		},
		setMultipleExecution (state, multipleExecution: boolean) {
			Vue.set(state, 'multipleExecution', multipleExecution);
		},
		setErrorWorkflow(state, errorWorkflow: string) {
			Vue.set(state, 'errorWorkflow', errorWorkflow);
		},
		setExecutionTimeout (state, executionTimeout: number) {
			Vue.set(state, 'executionTimeout', executionTimeout);
		},
		setMaxExecutionTimeout (state, maxExecutionTimeout: number) {
			Vue.set(state, 'maxExecutionTimeout', maxExecutionTimeout);
		},
		setInstanceId(state, instanceId: string) {
			Vue.set(state, 'instanceId', instanceId);
		},
		setTelemetry(state, telemetry: ITelemetrySettings) {
			Vue.set(state, 'telemetry', telemetry);
		},
		setOauthCallbackUrls(state, urls: IDataObject) {
			Vue.set(state, 'oauthCallbackUrls', urls);
		},
		setN8nMetadata(state, metadata: IDataObject) {
			Vue.set(state, 'n8nMetadata', metadata);
		},
		setDefaultLocale(state, locale: string) {
			Vue.set(state, 'defaultLocale', locale);
		},
		setActiveNode (state, nodeName: string) {
			state.activeNode = nodeName;
		},
		setActiveCredentialType (state, activeCredentialType: string) {
			state.activeCredentialType = activeCredentialType;
		},

		setLastSelectedNode (state, nodeName: string) {
			state.lastSelectedNode = nodeName;
		},

		setWorkflowModifiedDate (state, date: string) {
			state.WorkflowModifiedDate = date;
		},

		setLastSelectedNodeOutputIndex (state, outputIndex: number | null) {
			state.lastSelectedNodeOutputIndex = outputIndex;
		},
		setWorkflowVersionData (state, workflowVersionData) {
			state.workflowVersionData = workflowVersionData;
		},
		setVersionInfo (state, versionInfo: { nomePublicacao: string | null; descricaoPublicacao: string | null }) {
			state.versionInfo = versionInfo;
		},
		setVersionToPublish (state, versionId: string | null) {
			state.versionToPublish = versionId;
		},
		setWorkflowIdToPublish (state, workflowId: string | null) {
			state.workflowIdToPublish = workflowId;
		},
		setVersionWorkflowDataToEdit (state, workflowData: { nodes: any[], connections: any } | null) {
			state.versionWorkflowDataToEdit = workflowData;
		},

		setWorkflowExecutionData (state, workflowResultData: IExecutionResponse | null) {
			state.workflowExecutionData = workflowResultData;
		},
		addNodeExecutionData (state, pushData: IPushDataNodeExecuteAfter): void {
			if (state.workflowExecutionData === null) {
				throw new Error('The "workflowExecutionData" is not initialized!');
			}
			if (state.workflowExecutionData.data.resultData.runData[pushData.nodeName] === undefined) {
				Vue.set(state.workflowExecutionData.data.resultData.runData, pushData.nodeName, []);
			}
			state.workflowExecutionData.data.resultData.runData[pushData.nodeName].push(pushData.data);
		},
		clearNodeExecutionData (state, nodeName: string): void {
			if (state.workflowExecutionData === null) {
				return;
			}

			Vue.delete(state.workflowExecutionData.data.resultData.runData, nodeName);
		},

		clearWorkflowExecutionData (state): void {
			if (state.workflowExecutionData === null) {
				return;
			}

			Vue.set(state, 'workflowExecutionData', {})
		},

		setWorkflowSettings (state, workflowSettings: IWorkflowSettings) {
			Vue.set(state.workflow, 'settings', workflowSettings);
		},

		setWorkflowTagIds (state, tags: string[]) {
			Vue.set(state.workflow, 'tags', tags);
		},

		removeWorkflowTagId (state, tagId: string) {
			const tags = state.workflow.tags as string[];
			const updated = tags.filter((id: string) => id !== tagId);

			Vue.set(state.workflow, 'tags', updated);
		},

		// Workflow
		setWorkflow (state, workflow: IWorkflowDb) {
			Vue.set(state, 'workflow', workflow);

			if (!state.workflow.hasOwnProperty('active')) {
				Vue.set(state.workflow, 'active', false);
			}
			if (!state.workflow.hasOwnProperty('connections')) {
				Vue.set(state.workflow, 'connections', {});
			}
			if (!state.workflow.hasOwnProperty('createdAt')) {
				Vue.set(state.workflow, 'createdAt', -1);
			}
			if (!state.workflow.hasOwnProperty('updatedAt')) {
				Vue.set(state.workflow, 'updatedAt', -1);
			}
			if (!state.workflow.hasOwnProperty('id')) {
				Vue.set(state.workflow, 'id', PLACEHOLDER_EMPTY_WORKFLOW_ID);
			}
			if (!state.workflow.hasOwnProperty('nodes')) {
				Vue.set(state.workflow, 'nodes', []);
			}
			if (!state.workflow.hasOwnProperty('settings')) {
				Vue.set(state.workflow, 'settings', {});
			}
		},

		updateNodeTypes (state, nodeTypes: INodeTypeDescription[]) {
			const oldNodesToKeep = state.nodeTypes.filter(node => !nodeTypes.find(n => n.name === node.name && n.version === node.version));
			const newNodesState = [...oldNodesToKeep, ...nodeTypes];
			Vue.set(state, 'nodeTypes', newNodesState);
			state.nodeTypes = newNodesState;
		},

		addSidebarMenuItems (state, menuItems: IMenuItem[]) {
			const updated = state.sidebarMenuItems.concat(menuItems);
			Vue.set(state, 'sidebarMenuItems', updated);
		},
	},
	actions: {
		saveHistory({ commit }, data) {
			commit('addToHistory', data);
		},
		saveCurrentHistory({ commit }, data) {
			commit('addCurrentHistory', data);
		},
		undo({ commit }) {
			commit('undoAction');
		},
		redo({ commit }) {
			commit('redoAction');
		},
	},
	getters: {
		activeCredentialType: (state): string | null => {
			return state.activeCredentialType;
		},

		currentHistory(state) {
			return cloneDeep(state.history.current);
		},
		undoHistory(state) {
			return state.history.undo;
		},
		redoHistory(state) {
			return state.history.redo;
		},

		alertData: (state) => {
			return state.alertData;
		},
		showLoadingCredentials: (state) => {
			return state.showLoadingCredentials;
		},

		data403: (state) => {
			return state.data403;
		},

		isActionActive: (state) => (action: string): boolean => {
			return state.activeActions.includes(action);
		},

		isNewWorkflow: (state) => {
			return state.workflow.id === PLACEHOLDER_EMPTY_WORKFLOW_ID;
		},

		isTelemetryEnabled: (state) => {
			return state.telemetry && state.telemetry.enabled;
		},

		currentWorkflowHasWebhookNode: (state: IRootState): boolean => {
			return !!state.workflow.nodes.find((node: INodeUi) => !!node.webhookId);
		},

		getActiveExecutions: (state): IExecutionsCurrentSummaryExtended[] => {
			return state.activeExecutions;
		},

		getBaseUrl: (state): string => {
			return state.baseUrl;
		},
		getRestUrl: (state): string => {
			/*let endpoint = 'v1';
			if (process.env.VUE_APP_ENDPOINT_REST) {
				endpoint = process.env.VUE_APP_ENDPOINT_REST;
			}*/
			return `${state.baseUrl}`;
		},
		getRestApiContext(state): IRestApiContext {
			/*let endpoint = 'v1';
			if (process.env.VUE_APP_ENDPOINT_REST) {
				endpoint = process.env.VUE_APP_ENDPOINT_REST;
			}*/
			return {
				baseUrl: `${state.baseUrl}`,
				sessionId: state.sessionId,
				tenantId: state.tenantId,
			};
		},
		getWebhookBaseUrl: (state): string => {
			return state.urlBaseWebhook;
		},
		getWebhookUrl: (state): string => {
			return `${state.urlBaseWebhook}/${state.endpointWebhook}`;
		},
		getWebhookTestUrl: (state): string => {
			return `${state.urlBaseWebhook}/${state.endpointWebhookTest}`;
		},

		getStateIsDirty: (state) : boolean => {
			return state.stateIsDirty;
		},

		hasUnpublishedChanges: (state): boolean => {
			return state.hasUnpublishedChanges;
		},

		instanceId: (state): string => {
			return state.instanceId;
		},

		saveDataErrorExecution: (state): string => {
			return state.saveDataErrorExecution;
		},
		saveDataSuccessExecution: (state): string => {
			return state.saveDataSuccessExecution;
		},
		saveManualExecutions: (state): boolean => {
			return state.saveManualExecutions;
		},
		erroWorkflow: (state): string => {
			return state.errorWorkflow;
		},
		timezone: (state): string => {
			return state.timezone;
		},
		executionTimeout: (state): number => {
			return state.executionTimeout;
		},
		maxExecutionTimeout: (state): number => {
			return state.maxExecutionTimeout;
		},
		telemetry: (state): ITelemetrySettings | null => {
			return state.telemetry;
		},
		oauthCallbackUrls: (state): object => {
			return state.oauthCallbackUrls;
		},
		n8nMetadata: (state): object => {
			return state.n8nMetadata;
		},
		defaultLocale: (state): string => {
			return state.defaultLocale;
		},

		// Push Connection
		pushConnectionActive: (state): boolean => {
			return state.pushConnectionActive;
		},
		sessionId: (state): string => {
			return state.sessionId;
		},
		tenantId: (state): string => {
			return state.tenantId;
		},

		// Active Workflows
		getActiveWorkflows: (state): string[] => {
			return state.activeWorkflows;
		},

		getSelectedFunctionalities: (state): string[] => {
			return state.selectedFunctionalities;
		},


		workflowTriggerNodes: (state, getters) => {
			return state.workflow.nodes.filter(node => {
				return getters.nodeType(node.type).group.includes('trigger');
			});
		},

		hasTriggerNode: (state, getters) => {
			for (const node of state.workflow.nodes) {
				if ((node.type.toLowerCase() == "n8n-nodes-base.cron" || node.type.toLowerCase() == "n8n-nodes-base.webhook" || node.type.toLowerCase() == "n8n-nodes-base.interval" || node.type.toLowerCase() == "n8n-nodes-base.emailreadimap" || node.type.toLowerCase().includes('trigger')) && !node.disabled) {
					return true;
				}
			}
			return false;
		},

		// Node-Index
		getNodeIndex: (state) => (nodeName: string): number => {
			return state.nodeIndex.indexOf(nodeName);
		},
		getNodeNameByIndex: (state) => (index: number): string | null => {
			return state.nodeIndex[index];
		},

		getNodeViewOffsetPosition: (state): XYPosition => {
			return state.nodeViewOffsetPosition;
		},
		isNodeViewMoveInProgress: (state): boolean => {
			return state.nodeViewMoveInProgress;
		},


		// Selected Nodes
		getSelectedNodes: (state): INodeUi[] => {
			return state.selectedNodes;
		},

		getNodesIntegration: (state): INodeUi[] => {
			return state.nodesIntegration;
		},

		isNodeSelected: (state) => (nodeName: string): boolean => {
			let index;
			for (index in state.selectedNodes) {
				if (state.selectedNodes[index].name === nodeName) {
					return true;
				}
			}
			return false;
		},

		isActive: (state): boolean => {
			return state.workflow.active;
		},
		allConnections: (state): IConnections => {
			return state.workflow.connections;
		},
		outgoingConnectionsByNodeName: (state) => (nodeName: string): INodeConnections => {
			if (state.workflow.connections.hasOwnProperty(nodeName)) {
				return state.workflow.connections[nodeName];
			}
			return {};
		},
		allNodes: (state): INodeUi[] => {
			return state.workflow.nodes;
		},
		nodesByName: (state: IRootState): {[name: string]: INodeUi} => {
			return state.workflow.nodes.reduce((accu: {[name: string]: INodeUi}, node) => {
				accu[node.name] = node;
				return accu;
			}, {});
		},
		allNodesByName: (state: IRootState): {[name: string]: INodeUi} => {
			return state.nodeTypes.reduce((accu: {[name: string]: INodeUi}, node) => {
				accu[node.name] = node;
				return accu;
			}, {});
		},
		getAllNodeByName: (state, getters) => (nodeName: string): INodeUi | null => {
			let node = JSON.parse(JSON.stringify(getters.allNodesByName[nodeName] || null));
			if (node?.type == "n8n-nodes-base.executeWorkflow" || node?.type == 'n8n-nodes-base.equivalencias' || node?.type == "n8n-nodes-base.variaveis" || node?.type == "n8n-nodes-base.alerta") {
				const token = TokenService.getTokens();
				node.parameters.token = token.access_token;
				node.parameters.projetoId = state.workflowData.projetoId;
				node.parameters.tenantId = WebStorage.getItem('tenantId');
				node.parameters.workspaceId = JSON.parse(WebStorage.getItem('workspace')).id;
				node.parameters.workflowId = state.workflowData.id;
			};
			return node;
		},
		getNodeByName: (state, getters) => (nodeName: string): INodeUi | null => {
			let node = JSON.parse(JSON.stringify(getters.nodesByName[nodeName] || null));
			if (node?.type == "n8n-nodes-base.executeWorkflow" || node?.type == 'n8n-nodes-base.equivalencias' || node?.type == "n8n-nodes-base.variaveis" || node?.type == "n8n-nodes-base.alerta") {
				const token = TokenService.getTokens();
				node.parameters.token = token.access_token;
				node.parameters.projetoId = state.workflowData.projetoId;
				node.parameters.tenantId = WebStorage.getItem('tenantId');
				node.parameters.workspaceId = JSON.parse(WebStorage.getItem('workspace')).id;
				node.parameters.workflowId = state.workflowData.id || state.workflow.id;
			};
			return node;
		},
		nodesIssuesExist: (state): boolean => {
			for (const node of state.workflow.nodes) {
				if (node.issues === undefined || Object.keys(node.issues).length === 0) {
					continue;
				}
				return true;
			}
			return false;
		},
		allNodeTypes: (state): INodeTypeDescription[] => {
			return state.nodeTypes;
		},

		/**
		 * Getter for node default names ending with a number: `'S3'`, `'Magento 2'`, etc.
		 */
		nativelyNumberSuffixedDefaults: (_, getters): string[] => {
			const { allNodeTypes } = getters as {
				allNodeTypes: Array<INodeTypeDescription & { defaults: { name: string } }>;
			};

			return allNodeTypes.reduce<string[]>((acc, cur) => {
				if (/\d$/.test(cur.defaults.name)) acc.push(cur.defaults.name);
				return acc;
			}, []);
		},

		nodeType: (state, getters) => (nodeType: string, typeVersion?: number): INodeTypeDescription | null => {
			const foundType = state.nodeTypes.find(typeData => {
				return typeData.name === nodeType && typeData.version === (typeVersion || typeData.defaultVersion || DEFAULT_NODETYPE_VERSION);
			});

			if (foundType === undefined) {
				return null;
			}
			return foundType;
		},
		activeNode: (state, getters): INodeUi | null => {
			return getters.getNodeByName(state.activeNode);
		},
		lastSelectedNode: (state, getters): INodeUi | null => {
			return getters.getNodeByName(state.lastSelectedNode);
		},
		lastSelectedNodeOutputIndex: (state, getters): number | null => {
			return state.lastSelectedNodeOutputIndex;
		},

		// Active Execution
		executingNode: (state): string | null => {
			return state.executingNode;
		},

		credentialOptions: (state): any[] | null => {
			return state.credentialOptions;
		},

		activeExecutionId: (state): string | null => {
			return state.executionId;
		},
		executionWaitingForWebhook: (state): boolean => {
			return state.executionWaitingForWebhook;
		},

		workflowCanUndo: (state): boolean => {
			return state.workflowCanUndo;
		},

		workflowCanRedo: (state): boolean => {
			return state.workflowCanRedo;
		},

		workflowName: (state): string => {
			return state.workflow.name;
		},

		workflowAtivo: (state): boolean => {
			return state.workflowAtivo;
		},
		originalDataWorkflow: (state): object => {
			return state.originalDataWorkflow;
		},
		workflowSaveAutomatic: (state): boolean => {
			return state.workflowSaveAutomatic;
		},
		workflowId: (state): string => {
			return state.workflow.id;
		},

		workspace: (state): object => {
			return state.workspace;
		},

		filters: (state): object => {
			return state.filters;
		},

		multiAccount: (state): boolean => {
			return state.multiAccount;
		},

		routeName: (state): string => {
			return state.routeName;
		},
		projectName: (state): string => {
			return state.projectName;
		},

		hasChangeOnNode: (state): boolean => {

			const data = {
				active: state.workflow.active,
				name: state.workflow.name,
				nodes: state.workflow.nodes,
				connections: state.workflow.connections,
				settings: state.workflow.settings,
				tags: state.workflow.tags,
			};

			const originalDataWorkflow = JSON.parse(JSON.stringify(state.originalDataWorkflow));
			const currentWorkflowData = JSON.parse(JSON.stringify(data));


			let originalStringData = JSON.stringify(originalDataWorkflow.connections) + JSON.stringify(originalDataWorkflow.active) + JSON.stringify(originalDataWorkflow.settings) + JSON.stringify(originalDataWorkflow.nodes) + JSON.stringify(originalDataWorkflow.name);
			let currentStringData = JSON.stringify(currentWorkflowData.connections) + JSON.stringify(currentWorkflowData.active) + JSON.stringify(currentWorkflowData.settings) + JSON.stringify(currentWorkflowData.nodes) + JSON.stringify(currentWorkflowData.name);

			const hasChangeOnData = originalStringData !== currentStringData;

			return hasChangeOnData;
		},



		workflowDataId: (state): string => {
			return state.workflowData.id;
		},

		projectData: (state): object => {
			return state.projectData;
		},

		workflowPermissionEdit: (state): boolean => {
			return state.workflowData.permission == 'Editar';
		},

		workflowData: (state): object => {
			return state.workflowData;
		},

		loadingData: (state): object => {
			return state.loadingData;
		},
		modalData: (state): object => {
			return state.modalData;
		},

		userData: (state): object => {
			return state.userData
		},

		sendUserData: (state): object => {
			return state.sendUserData
		},

		dataFilterPanel: (state): object => {
			return state.dataFilterPanel
		},

		userModalName: (state): string => {
			return state.userModalName
		},

		accountName: (state): string => {
			return state.accountName;
		},


		getWorkflowModifiedDate: (state): string => {
			return state.WorkflowModifiedDate;
		},

		getWorkflowProjectId:  (state): string | number => {
			return state.workflowData.projetoId;
		},


		workflowSettings: (state): IWorkflowSettings => {
			if (state.workflow.settings === undefined) {
				return {};
			}
			return state.workflow.settings;
		},

		workflowTags: (state): string[] => {
			return state.workflow.tags as string[];
		},

		// Workflow Result Data
		getWorkflowExecution: (state): IExecutionResponse | null => {
			return state.workflowExecutionData;
		},

		getWorkflowVersionData: (state): IExecutionResponse | null => {
			return state.workflowVersionData;
		},
		versionInfo: (state): { nomePublicacao: string | null; descricaoPublicacao: string | null } => {
			return state.versionInfo;
		},
		getWorkflowRunData: (state): IRunData | null => {
			if (!state.workflowExecutionData || !state.workflowExecutionData.data || !state.workflowExecutionData.data.resultData) {
				return null;
			}

			return state.workflowExecutionData.data.resultData.runData;
		},
		getWorkflowResultDataByNodeName: (state, getters) => (nodeName: string): ITaskData[] | null => {
			const workflowRunData = getters.getWorkflowRunData;

			if (workflowRunData === null) {
				return null;
			}
			if (!workflowRunData.hasOwnProperty(nodeName)) {
				return null;
			}
			return workflowRunData[nodeName];
		},

		sidebarMenuItems: (state): IMenuItem[] => {
			return state.sidebarMenuItems;
		},
	},
});
